import { api } from '../../../../api/client';

export const normalizeServerMessage = (message) => {
  if (!message) return null;
  const sanitized = String(message)
    .replace(/(?:stack|trace|syntax|select|update|insert|delete|from|where|error:).*$/i, '')
    .trim();
  return sanitized || null;
};

export const statusToDisplayMessage = (status, fallback) => {
  switch (status) {
    case 400:
      return '入力内容に不備があります。内容を確認してください。';
    case 401:
      return 'セッションの有効期限が切れました。再ログインしてください。';
    case 403:
      return 'この操作を実行する権限がありません。';
    case 404:
      return '対象が見つかりません。';
    case 409:
      return '他の更新と衝突しました。ページを再読み込みして再試行してください。';
    case 413:
      return 'ファイルサイズが大きすぎます。上限以内に調整してください。';
    case 415:
      return '対応していないファイル形式です。';
    case 422:
      return '入力内容を確認してください。';
    case 429:
      return 'リクエストが多すぎます。しばらくしてから再試行してください。';
    default:
      if (status >= 500) {
        return 'サーバーでエラーが発生しました。時間を置いて再度お試しください。';
      }
      return fallback || 'エラーが発生しました。';
  }
};

const normalizeEventChatRole = (role) =>
  String(role || '').toLowerCase() === 'admin' ? 'admin' : 'artist';

const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const normalizeApiPath = (url) => {
  const text = String(url || '').trim();
  if (!text) return '/';
  const withoutOrigin = API_ORIGIN && text.startsWith(API_ORIGIN) ? text.slice(API_ORIGIN.length) : text;
  return withoutOrigin.startsWith('/api/') ? withoutOrigin.slice(4) : withoutOrigin;
};

const readApiPayload = async (data, response) => {
  if (data && typeof data === 'object') return data;
  if (typeof data === 'string') {
    try {
      return data ? JSON.parse(data) : {};
    } catch (_) {
      return {};
    }
  }
  try {
    const text = await response.clone().text();
    return text ? JSON.parse(text) : {};
  } catch (_) {
    return {};
  }
};

const requestJsonViaApiClient = async (url, opts = {}) => {
  const method = String(opts.method || 'GET').toUpperCase();
  const path = normalizeApiPath(url);
  const requestOptions = {
    ...opts,
    parseAs: 'text',
  };
  delete requestOptions.method;

  if (method === 'POST') return api.POST(path, requestOptions);
  if (method === 'PUT') return api.PUT(path, requestOptions);
  if (method === 'PATCH') return api.PATCH(path, requestOptions);
  if (method === 'DELETE') return api.DELETE(path, requestOptions);
  return api.GET(path, requestOptions);
};

export const buildEventChatSearchUrl = ({
  apiUrl,
  role,
  q = '',
  message = '',
  startDate = '',
  endDate = '',
  offset = 0,
  limit = 100,
  snapshotToken = '',
} = {}) => {
  const safeApiUrl = String(apiUrl || '').replace(/\/$/, '');
  const safeRole = normalizeEventChatRole(role);
  const sp = new URLSearchParams();
  const qText = String(q || '').trim();
  const messageText = String(message || '').trim();
  const startDateText = String(startDate || '').trim();
  const endDateText = String(endDate || '').trim();
  const snapshotTokenText = String(snapshotToken || '').trim();
  if (qText) sp.set('q', qText);
  if (messageText) sp.set('message', messageText);
  if (startDateText) sp.set('startDate', startDateText);
  if (endDateText) sp.set('endDate', endDateText);
  sp.set('offset', String(offset));
  sp.set('limit', String(limit));
  // 追加ページは同一スナップショット上で取得し、検索結果のズレ（重複/取りこぼし）を防ぐ
  if (snapshotTokenText) sp.set('snapshot_token', snapshotTokenText);
  return `${safeApiUrl}/api/${safeRole}/event-chat/search?${sp.toString()}`;
};

export const createFetchJsonSafe = ({
  useStatusMessage = false,
  networkErrorMessage = '',
  defaultErrorMessage = 'リクエストに失敗しました',
} = {}) => {
  return async (url, opts = {}, { label } = {}) => {
    try {
      const { data, response: res } = await requestJsonViaApiClient(url, opts);
      const payload = await readApiPayload(data, res);

      if (!res.ok) {
        const raw = payload?.error || payload?.message;
        const normalized = normalizeServerMessage(raw);
        const fallbackMessage = useStatusMessage
          ? statusToDisplayMessage(res.status, label)
          : label || defaultErrorMessage;
        const err = new Error(normalized || fallbackMessage);
        err.status = res.status;
        throw err;
      }
      return payload || {};
    } catch (error) {
      if (networkErrorMessage && !error?.status) {
        const networkError = new Error(networkErrorMessage);
        networkError.cause = error;
        throw networkError;
      }
      throw error;
    }
  };
};
